import { useId, useState, type FC } from 'react';
import type { SampleTestDto } from '@/api';
import { Alert, Button, Dialog, Textarea } from '@/ui';

import { useFailSampleTest, useSucceedSampleTest } from './api/mutations';
import { describeSampleTest } from './lib/describe';
import { canSubmitClose, toCloseBody } from './lib/draft';
import { resolveSampleTestErrorMessage } from './lib/errors';

/*
 * Итог теста - «Одобрено» или «Не подошло» с обязательным комментарием: почему
 * подошло или нет. Он заменяет прежний, поэтому поле пустое, а прежний показан
 * над ним. Ошибся с итогом - «Вернуть в тестирование» в строке.
 */

export type SampleTestOutcome = 'Succeeded' | 'Failed';

const OUTCOMES: Record<
  SampleTestOutcome,
  { title: string; action: string; placeholder: string }
> = {
  Succeeded: {
    title: 'Образец одобрен',
    action: 'Одобрено',
    placeholder: 'Подошёл по вкусу и текстуре, клиент готов заказывать...',
  },
  Failed: {
    title: 'Образец не подошёл',
    action: 'Не подошло',
    placeholder: 'Не подошло по кислотности...',
  },
};

export interface CloseSampleTestDialogProps {
  test: SampleTestDto;
  outcome: SampleTestOutcome;
  onClose: () => void;
}

export const CloseSampleTestDialog: FC<CloseSampleTestDialogProps> = ({
  test,
  outcome,
  onClose,
}) => {
  const commentId = useId();
  const [comment, setComment] = useState('');
  const succeed = useSucceedSampleTest();
  const fail = useFailSampleTest();
  const close = outcome === 'Succeeded' ? succeed : fail;
  const texts = OUTCOMES[outcome];

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={texts.title}
      description={describeSampleTest(test)}
      widthClassName="w-[min(480px,90vw)]"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant={outcome === 'Succeeded' ? 'primary' : 'danger'}
            disabled={!canSubmitClose(comment) || close.isPending}
            onClick={() =>
              close.mutate(
                { path: { id: test.id }, body: toCloseBody(comment) },
                { onSuccess: onClose },
              )
            }
          >
            {texts.action}
          </Button>
        </>
      }
    >
      {test.comment && (
        <p className="m-0 mb-3 text-[13px] text-ink-600">
          Сейчас в комментарии: «{test.comment}». Комментарий к итогу его
          заменит.
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <label htmlFor={commentId} className="text-xs font-medium text-ink-700">
          Комментарий к итогу
        </label>
        <Textarea
          id={commentId}
          placeholder={texts.placeholder}
          maxLength={1000}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </div>
      {close.isError && (
        <Alert tone="error" className="mt-3.5">
          Не удалось сохранить итог:{' '}
          {resolveSampleTestErrorMessage(close.error)}
        </Alert>
      )}
    </Dialog>
  );
};
